import { Box, Flex, Text } from '@inflearn/ds-react';
import { random } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ASSETS,
  type Position,
  RED_LIGHT_GREEN_LIGHT_CONFIG,
  type WatcherState,
  calculateDistance,
  getRandomDuration,
} from '../../../../constants/redLightGreenLightConfig';
import { RedLightGameBackground } from './RedLightGameBackground';
import { RedLightGameHUD } from './RedLightGameHUD';
import { RedLightGamePlayer } from './RedLightGamePlayer';
import { RedLightGameStartScreen } from './RedLightGameStartScreen';
import { RedLightGameWatcher } from './RedLightGameWatcher';
import { gameWrapperStyle, redLightGameAreaStyle } from './styles';
import type { Decoration, RedLightGreenLightGameProps } from './types';

export const RedLightGreenLightGame = ({
  isGameStarted,
  onGameStart,
  onGameOver,
}: RedLightGreenLightGameProps) => {
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [playerX, setPlayerX] = useState(RED_LIGHT_GREEN_LIGHT_CONFIG.PLAYER_START_X);
  const [playerY, setPlayerY] = useState(RED_LIGHT_GREEN_LIGHT_CONFIG.PLAYER_START_Y);
  const [isWalking, setIsWalking] = useState(false);
  const [walkFrame, setWalkFrame] = useState(0);
  const [isCaught, setIsCaught] = useState(false);
  const [watcherState, setWatcherState] = useState<WatcherState>('green');
  const [_isGracePeriod, setIsGracePeriod] = useState(false);
  const [isPreloading, setIsPreloading] = useState(false);
  const [isGameOver, setIsGameOver] = useState(false);
  const [redStateDuration, setRedStateDuration] = useState(0);
  const [blinkState, setBlinkState] = useState(false);
  const [decorations] = useState<Decoration[]>([
    { id: 0, x: 100, y: 100, imageUrl: ASSETS.decorations[0], size: 60 },
    { id: 1, x: 250, y: 150, imageUrl: ASSETS.decorations[1], size: 70 },
    { id: 2, x: 150, y: 300, imageUrl: ASSETS.decorations[2], size: 65 },
    { id: 3, x: 600, y: 100, imageUrl: ASSETS.decorations[3], size: 60 },
    { id: 4, x: 700, y: 250, imageUrl: ASSETS.decorations[0], size: 55 },
    { id: 5, x: 550, y: 350, imageUrl: ASSETS.decorations[1], size: 70 },
    { id: 6, x: 350, y: 450, imageUrl: ASSETS.decorations[2], size: 65 },
    { id: 7, x: 450, y: 200, imageUrl: ASSETS.decorations[3], size: 60 },
    { id: 8, x: 200, y: 500, imageUrl: ASSETS.decorations[0], size: 55 },
    { id: 9, x: 650, y: 450, imageUrl: ASSETS.decorations[1], size: 70 },
  ]);

  const watcherCycleRef = useRef<NodeJS.Timeout | null>(null);
  const gracePeriodRef = useRef<NodeJS.Timeout | null>(null);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const redStateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const blinkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const keysPressed = useRef<Set<string>>(new Set());
  const lastStepKey = useRef<'w' | 'e' | null>(null);
  const startTimeRef = useRef<number>(0);

  // Reset state when game starts
  useEffect(() => {
    if (isGameStarted) {
      startTimeRef.current = performance.now();
      setElapsedTime(0);
      setPlayerX(RED_LIGHT_GREEN_LIGHT_CONFIG.PLAYER_START_X);
      setPlayerY(RED_LIGHT_GREEN_LIGHT_CONFIG.PLAYER_START_Y);
      setIsWalking(false);
      setWalkFrame(0);
      setIsCaught(false);
      setWatcherState('green');
      setIsGracePeriod(false);
      setIsGameOver(false);
      setRedStateDuration(0);
      setBlinkState(false);
      keysPressed.current.clear();
      lastStepKey.current = null;
    }
  }, [isGameStarted]);

  // Movement Handler
  const movePlayer = useCallback((_direction: 'right') => {
    setIsWalking(true);
    setPlayerX((prev) => {
      const newX = Math.min(
        prev + RED_LIGHT_GREEN_LIGHT_CONFIG.MOVEMENT_SPEED,
        RED_LIGHT_GREEN_LIGHT_CONFIG.BOARD_WIDTH - 20,
      );
      return newX;
    });
    setTimeout(() => {
      setIsWalking(false);
    }, 150);
  }, []);

  // Game Over Handler
  const handleGameOver = useCallback(
    (result: 'win' | 'lose', currentElapsedTimeMs: number, currentIsCaught: boolean) => {
      // Cleanup intervals first
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (watcherCycleRef.current) {
        clearTimeout(watcherCycleRef.current);
      }
      if (gracePeriodRef.current) {
        clearTimeout(gracePeriodRef.current);
      }

      if (result === 'win') {
        // 걸린 시간(밀리초)을 점수로 사용
        onGameOver(result, currentElapsedTimeMs, false);
      } else {
        onGameOver(result, 0, currentIsCaught);
      }
    },
    [onGameOver],
  );

  const handleCaught = useCallback(() => {
    setIsGameOver(true);
    setIsCaught(true);
    // Use setTimeout to ensure state updates are processed
    setTimeout(() => {
      const currentElapsedMs = Math.floor(performance.now() - startTimeRef.current);
      handleGameOver('lose', currentElapsedMs, true);
    }, 0);
  }, [handleGameOver]);

  // Asset Preloading
  useEffect(() => {
    const preloadImages = async () => {
      const imageUrls = [
        ASSETS.player.idle,
        ASSETS.player.walk1,
        ASSETS.player.walk2,
        ASSETS.watcher.green,
        ASSETS.watcher.peeking,
        ASSETS.watcher.sleeping,
        ASSETS.watcher.turning,
        ASSETS.watcher.red,
        ASSETS.environment.background,
        ...ASSETS.decorations,
      ];

      try {
        await Promise.all(
          imageUrls.map(
            (url) =>
              new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = resolve;
                img.onerror = reject;
                img.src = url;
              }),
          ),
        );
        setIsPreloading(false);
      } catch (error) {
        console.error('Failed to preload images:', error);
        setIsPreloading(false);
      }
    };

    preloadImages();
  }, []);

  // Timer Countup (밀리초 단위로 정확하게 측정)
  useEffect(() => {
    if (!isGameStarted || isGameOver) {
      return;
    }

    timerIntervalRef.current = setInterval(() => {
      const currentTime = performance.now();
      const elapsed = Math.floor(currentTime - startTimeRef.current);
      setElapsedTime(elapsed);
    }, 10); // 10ms마다 업데이트

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [isGameStarted, isGameOver]);

  // Watcher Behavior Cycle (변칙적이고 빠른 패턴 - 다양한 페이크)
  useEffect(() => {
    if (!isGameStarted || isGameOver) {
      return;
    }

    const scheduleNextState = () => {
      if (watcherState === 'green') {
        // 뒤를 보고 있는 상태 -> 랜덤한 시간 후 옆을 힐끗 봄
        const greenDuration = getRandomDuration(500, 2500); // 더 빠르고 변칙적

        watcherCycleRef.current = setTimeout(() => {
          setWatcherState('peeking');
        }, greenDuration);
      } else if (watcherState === 'peeking') {
        // 옆을 힐끗 보는 상태 -> 다양한 패턴 (페이크 포함)
        const peekDuration = getRandomDuration(100, 400); // 매우 짧은 시간

        watcherCycleRef.current = setTimeout(() => {
          const rand = random();

          if (rand < 0.25) {
            // 25% 확률로 다시 뒤를 봄 (페이크 - 안전)
            setWatcherState('green');
          } else if (rand < 0.4) {
            // 15% 확률로 조는 척 (안전)
            setWatcherState('sleeping');
          } else if (rand < 0.65) {
            // 25% 확률로 빠르게 돌아봄 (위험!)
            setWatcherState('turning');
            setTimeout(() => {
              setWatcherState('red');
              setRedStateDuration(0);
              setIsGracePeriod(true);

              gracePeriodRef.current = setTimeout(() => {
                setIsGracePeriod(false);
              }, RED_LIGHT_GREEN_LIGHT_CONFIG.GRACE_PERIOD);
            }, 80); // 매우 빠른 전환
          } else if (rand < 0.85) {
            // 20% 확률로 초고속 돌아봄 (극위험!)
            setWatcherState('turning');
            setTimeout(() => {
              setWatcherState('red');
              setRedStateDuration(0);
              setIsGracePeriod(true);

              gracePeriodRef.current = setTimeout(() => {
                setIsGracePeriod(false);
              }, RED_LIGHT_GREEN_LIGHT_CONFIG.GRACE_PERIOD);
            }, 50); // 초고속 전환
          } else {
            // 15% 확률로 일반 속도로 돌아봄
            setWatcherState('turning');
            setTimeout(() => {
              setWatcherState('red');
              setRedStateDuration(0);
              setIsGracePeriod(true);

              gracePeriodRef.current = setTimeout(() => {
                setIsGracePeriod(false);
              }, RED_LIGHT_GREEN_LIGHT_CONFIG.GRACE_PERIOD);
            }, 200);
          }
        }, peekDuration);
      } else if (watcherState === 'sleeping') {
        // 잠든 상태 -> 랜덤한 시간 후 다시 뒤를 봄
        const rand = random();
        let sleepDuration;

        if (rand < 0.3) {
          // 30% 확률로 짧게 졸음 (조는 척 페이크)
          sleepDuration = getRandomDuration(800, 1500);
        } else {
          // 70% 확률로 깊게 잠듦 (안전)
          sleepDuration = getRandomDuration(2000, 3500);
        }

        watcherCycleRef.current = setTimeout(() => {
          setWatcherState('green');
        }, sleepDuration);
      } else if (watcherState === 'turning') {
        // 돌아보는 중 -> red로 빠르게 전환 (이미 위에서 처리됨)
      } else if (watcherState === 'red') {
        // 완전히 돌아본 상태 -> 다양한 지속 시간
        const rand = random();
        let redDuration;

        if (rand < 0.4) {
          // 40% 확률로 매우 짧게 (깜빡 돌아봄)
          redDuration = getRandomDuration(150, 500);
        } else if (rand < 0.7) {
          // 30% 확률로 짧게
          redDuration = getRandomDuration(500, 1200);
        } else if (rand < 0.9) {
          // 20% 확률로 보통
          redDuration = getRandomDuration(1200, 2000);
        } else {
          // 10% 확률로 길게 (함정)
          redDuration = getRandomDuration(2000, 3000);
        }

        watcherCycleRef.current = setTimeout(() => {
          setWatcherState('green');
          setIsGracePeriod(false);
          setRedStateDuration(0);
          setBlinkState(false);
        }, redDuration);
      }
    };

    scheduleNextState();

    return () => {
      if (watcherCycleRef.current) {
        clearTimeout(watcherCycleRef.current);
      }
      if (gracePeriodRef.current) {
        clearTimeout(gracePeriodRef.current);
      }
    };
  }, [watcherState, isGameStarted, isGameOver]);

  // Red state duration tracking and blinking effect
  useEffect(() => {
    if (!isGameStarted || isGameOver || watcherState !== 'red') {
      if (redStateTimerRef.current) {
        clearTimeout(redStateTimerRef.current);
      }
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current);
      }
      return;
    }

    // Start tracking red state duration
    redStateTimerRef.current = setTimeout(() => {
      setRedStateDuration(1000);
      // Start blinking after 1 second
      blinkIntervalRef.current = setInterval(() => {
        setBlinkState((prev) => !prev);
      }, 300); // Blink every 300ms
    }, 1000);

    return () => {
      if (redStateTimerRef.current) {
        clearTimeout(redStateTimerRef.current);
      }
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current);
      }
    };
  }, [watcherState, isGameStarted, isGameOver]);

  // Victory Detection
  useEffect(() => {
    if (!isGameStarted || isGameOver) {
      return;
    }

    const doorPosition: Position = {
      x: RED_LIGHT_GREEN_LIGHT_CONFIG.DOOR_X,
      y: RED_LIGHT_GREEN_LIGHT_CONFIG.DOOR_Y,
    };

    const playerPosition: Position = { x: playerX, y: playerY };
    const distance = calculateDistance(playerPosition, doorPosition);

    if (distance <= RED_LIGHT_GREEN_LIGHT_CONFIG.DOOR_THRESHOLD) {
      setIsGameOver(true);
      const currentElapsedMs = Math.floor(performance.now() - startTimeRef.current);
      handleGameOver('win', currentElapsedMs, false);
    }
  }, [playerX, playerY, isGameStarted, isGameOver, handleGameOver]);

  // Keyboard Input Handler
  useEffect(() => {
    if (!isGameStarted || isGameOver) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isGameOver) {
        return;
      }

      // Use e.code for physical key detection (works with Korean IME)
      const code = e.code.toLowerCase();

      // Map physical key codes to logical keys
      let key: 'w' | 'e' | null = null;
      if (code === 'keyw') {
        key = 'w';
      } else if (code === 'keye') {
        key = 'e';
      }

      if (!key) {
        return;
      }

      if (keysPressed.current.has(key)) {
        return;
      }
      keysPressed.current.add(key);

      // turning과 red 상태에서만 걸림
      if (watcherState === 'red' || watcherState === 'turning') {
        handleCaught();
        return;
      }

      // green, peeking, sleeping 상태에서는 움직일 수 있음
      if (watcherState !== 'green' && watcherState !== 'peeking' && watcherState !== 'sleeping') {
        return;
      }

      if (lastStepKey.current === null) {
        lastStepKey.current = key;
        movePlayer('right');
        setWalkFrame((prev) => (prev === 0 ? 1 : 0));
        return;
      }

      if (lastStepKey.current !== key) {
        lastStepKey.current = key;
        movePlayer('right');
        setWalkFrame((prev) => (prev === 0 ? 1 : 0));
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      const code = e.code.toLowerCase();

      if (code === 'keyw') {
        keysPressed.current.delete('w');
      } else if (code === 'keye') {
        keysPressed.current.delete('e');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      keysPressed.current.clear();
    };
  }, [isGameStarted, isGameOver, watcherState, handleCaught, movePlayer]);

  if (!isGameStarted) {
    return <RedLightGameStartScreen onGameStart={onGameStart} />;
  }

  if (isPreloading) {
    return (
      <Flex css={redLightGameAreaStyle} align="center" justify="center">
        <Text size="xl" fw={700}>
          게임 로딩중...
        </Text>
      </Flex>
    );
  }

  return (
    <div css={gameWrapperStyle}>
      <Box css={redLightGameAreaStyle}>
        <RedLightGameBackground
          backgroundUrl={ASSETS.environment.background}
          decorations={decorations}
        />
        <RedLightGameWatcher
          x={RED_LIGHT_GREEN_LIGHT_CONFIG.WATCHER_X}
          y={RED_LIGHT_GREEN_LIGHT_CONFIG.WATCHER_Y}
          watcherState={watcherState}
          redStateDuration={redStateDuration}
          blinkState={blinkState}
        />
        <RedLightGamePlayer
          x={playerX}
          y={playerY}
          isCaught={isCaught}
          isWalking={isWalking}
          walkFrame={walkFrame}
        />
        <RedLightGameHUD elapsedTime={elapsedTime} />
      </Box>
    </div>
  );
};
